
import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Lock, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { SubscriptionTier } from '../../types';

interface PaymentViewProps {
  plan: SubscriptionTier;
  onSuccess: () => void;
  onBack: () => void;
}

export const PaymentView: React.FC<PaymentViewProps> = ({ plan, onSuccess, onBack }) => {
  const [loading, setLoading] = useState(false);
  
  // Pre-filled form state for testing
  const [cardName, setCardName] = useState('Julian Casablancas');
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  const [expiry, setExpiry] = useState('12/25');
  const [cvv, setCvv] = useState('123');

  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onSuccess();
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-8 bg-white rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
        
        <div className="p-10 bg-slate-50 border-r border-slate-200">
           <button onClick={onBack} className="flex items-center space-x-2 text-slate-500 hover:text-slate-900 mb-10 font-bold transition-all">
             <ArrowLeft size={18} />
             <span>Back to Pricing</span>
           </button>

           <h2 className="text-2xl font-black text-slate-900 mb-6">Order Summary</h2>
           
           <div className="space-y-4 mb-10">
              <div className="flex justify-between items-center p-4 bg-white rounded-2xl border border-slate-200 shadow-sm">
                 <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Plan</p>
                    <p className="text-lg font-bold text-slate-900">{plan} Subscription</p>
                 </div>
                 <div className="text-right">
                    <p className="text-lg font-black text-blue-600">${plan === 'Free' ? 0 : plan === 'Pro' ? 249 : 999}</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase">per month</p>
                 </div>
              </div>
           </div>

           <div className="space-y-4 pt-4 border-t border-slate-200">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 font-medium">Subtotal</span>
                <span className="text-slate-900 font-bold">${plan === 'Free' ? 0 : plan === 'Pro' ? 249 : 999}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 font-medium">Tax (0%)</span>
                <span className="text-slate-900 font-bold">$0.00</span>
              </div>
              <div className="flex justify-between text-xl font-black pt-4 border-t border-slate-200">
                <span className="text-slate-900">Total</span>
                <span className="text-blue-600">${plan === 'Free' ? 0 : plan === 'Pro' ? 249 : 999}</span>
              </div>
           </div>
        </div>

        <div className="p-10 flex flex-col justify-center">
           <div className="mb-8">
             <h2 className="text-2xl font-black text-slate-900 mb-2">Secure Checkout</h2>
             <p className="text-slate-500 text-sm font-medium">Safe and encrypted payment processing.</p>
           </div>

           <form onSubmit={handlePayment} className="space-y-5">
              <div className="space-y-2">
                 <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Cardholder Name</label>
                 <input 
                  type="text" 
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none" 
                  placeholder="Julian Casablancas" 
                  required 
                 />
              </div>

              <div className="space-y-2">
                 <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Card Number</label>
                 <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input 
                      type="text" 
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none" 
                      placeholder="4242 4242 4242 4242" 
                      required 
                    />
                 </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                 <div className="space-y-2">
                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Expiry</label>
                    <input 
                      type="text" 
                      value={expiry}
                      onChange={(e) => setExpiry(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none" 
                      placeholder="MM/YY" 
                      required 
                    />
                 </div>
                 <div className="space-y-2">
                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">CVV</label>
                    <input 
                      type="text" 
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 outline-none" 
                      placeholder="123" 
                      required 
                    />
                 </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className={`w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-sm flex items-center justify-center space-x-3 transition-all active:scale-95 shadow-xl shadow-slate-200 ${loading ? 'opacity-70 cursor-wait' : 'hover:bg-slate-800'}`}
              >
                {loading ? (
                   <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                   <>
                     <Lock size={18} />
                     <span>Complete Purchase</span>
                   </>
                )}
              </button>
           </form>

           <div className="mt-8 flex items-center justify-center space-x-6">
              <div className="flex items-center space-x-1 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                 <ShieldCheck size={14} className="text-emerald-500" />
                 <span>PCI DSS Compliant</span>
              </div>
              <div className="flex items-center space-x-1 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                 <CheckCircle2 size={14} className="text-emerald-500" />
                 <span>SSL Encrypted</span>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};
